import type { TeamId } from '@/core/schemas';

import type { Seed } from '../../contracts';

/**
 * Spreads the seeded field across the groups.
 *
 * `snake` runs alternately left-to-right and right-to-left, so the strongest
 * and weakest teams are distributed evenly instead of all top seeds landing in
 * the first groups.
 */
export function distributeIntoGroups(
  participants: readonly Seed[],
  groupCount: number,
  distribution: 'snake' | 'sequential',
): TeamId[][] {
  const groups: TeamId[][] = Array.from({ length: groupCount }, () => []);

  if (distribution === 'sequential') {
    const perGroup = Math.ceil(participants.length / groupCount);
    participants.forEach((seed, index) => {
      groups[Math.min(Math.floor(index / perGroup), groupCount - 1)]?.push(seed.teamId);
    });
    return groups;
  }

  participants.forEach((seed, index) => {
    const pass = Math.floor(index / groupCount);
    const position = index % groupCount;
    const target = pass % 2 === 0 ? position : groupCount - 1 - position;
    groups[target]?.push(seed.teamId);
  });

  return groups;
}

export interface ScheduledPairing {
  readonly round: number;
  readonly home: TeamId;
  readonly away: TeamId;
}

/**
 * Round-robin schedule by the circle method: one team stays fixed, the others
 * rotate around it. Every team meets every other exactly once, and no team
 * plays twice in the same round — which matters because the boards are the
 * bottleneck on tournament day.
 *
 * With an odd number of teams a placeholder sits in, giving that team a bye in
 * the round it is drawn against.
 */
export function roundRobinPairings(teamIds: readonly TeamId[], doubleRound: boolean) {
  if (teamIds.length < 2) {
    return [] as ScheduledPairing[];
  }

  const BYE = null;
  const entries: (TeamId | null)[] = [...teamIds];

  if (entries.length % 2 !== 0) {
    entries.push(BYE);
  }

  const size = entries.length;
  const roundCount = size - 1;
  const pairings: ScheduledPairing[] = [];

  for (let round = 0; round < roundCount; round += 1) {
    for (let slot = 0; slot < size / 2; slot += 1) {
      const first = entries[slot];
      const second = entries[size - 1 - slot];

      if (first === BYE || second === BYE || first === undefined || second === undefined) {
        continue;
      }

      // Alternate home and away so no team is always the home side.
      const swap = round % 2 === 1;
      pairings.push({
        round,
        home: swap ? second : first,
        away: swap ? first : second,
      });
    }

    // Rotate everything except the first entry.
    const [fixed, ...rotating] = entries;
    const last = rotating.pop();
    if (fixed !== undefined && last !== undefined) {
      entries.length = 0;
      entries.push(fixed, last, ...rotating);
    }
  }

  if (!doubleRound) {
    return pairings;
  }

  const reverse = pairings.map((pairing) => ({
    round: pairing.round + roundCount,
    home: pairing.away,
    away: pairing.home,
  }));

  return [...pairings, ...reverse];
}
